import React, { useState, useEffect } from 'react';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, TextField, Button } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase'; // Adjust path as needed
import { textFieldStyles } from '../../Login/LoginPageStyles';
import TimesheetDialog from './TimesheetDialog'; // Import the TimesheetDialog component

const Tasks = ({ onAddTaskClick }) => {
  const [tasks, setTasks] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false); // State for dialog open/close
  const [selectedTaskId, setSelectedTaskId] = useState(null); // State for the selected task ID

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const tasksCollection = collection(db, 'assignedTasks');
        const tasksSnapshot = await getDocs(tasksCollection);
        const tasksList = tasksSnapshot.docs.map(doc => ({
          id: doc.data().taskId,
          employeeId: doc.data().employeeId,
          taskName: doc.data().taskName,
          assignedTo: doc.data().employee,
          status: doc.data().status,
          workHours: doc.data().workHours,
        }));
        setTasks(tasksList);
        setFilteredTasks(tasksList);
      } catch (error) {
        console.error('Error fetching tasks: ', error);
      }
    };

    fetchTasks();
  }, []);

  const handleSearchChange = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = tasks.filter(task => 
      task.status.toLowerCase().includes(query) ||
      task.taskName.toLowerCase().includes(query) ||
      task.assignedTo.toLowerCase().includes(query) ||
      task.employeeId.toLowerCase().includes(query) ||
      task.id.toLowerCase().includes(query)
    );
    setFilteredTasks(filtered);
  };

  const handleTimesheetClick = (taskId) => {
    setSelectedTaskId(taskId); // Set the selected task ID
    setIsDialogOpen(true); // Open the dialog
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false); // Close the dialog
    setSelectedTaskId(null); // Clear the selected task ID
  };

  const displayedData = filteredTasks.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  );

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusStyle = (status) => {
    if (status === 'completed') {
      return {
        backgroundColor: '#39B54A',
        color: 'white',
        padding: '5px 10px',
        borderRadius: '4px',
        textAlign: 'center',
      };
    } else if (status === 'ongoing') {
      return {
        backgroundColor: '#FFA500',
        color: 'white',
        padding: '5px 10px',
        borderRadius: '4px',
        textAlign: 'center',
      };
    } else if (status === 'pending') {
      return {
        backgroundColor: '#d3d3d3',
        color: 'black',
        padding: '5px 10px',
        borderRadius: '4px',
        textAlign: 'center',
      };
    }
  };

  return (
    <div>
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          padding: 3,
          marginTop: 2,
        }}
      >
        <Typography variant="h5">Tasks</Typography>
        <Box
          sx={{
            padding: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <TextField
            label="Search Tasks"
            variant="outlined"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ ...textFieldStyles, width: '400px', marginY: '2' }}
          />
          <Button
            variant="contained"
            sx={{ backgroundColor: '#39B54A', '&:hover': { backgroundColor: '#2c8f39' } }}
            startIcon={<AssignmentIcon />}
            onClick={onAddTaskClick}
          >
            ADD TASK
          </Button>
        </Box>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center"><b>Task ID</b></TableCell>
              <TableCell align="left" sx={{ width: '400px', whiteSpace: 'normal', wordWrap: 'break-word' }}><b>Task</b></TableCell>
              <TableCell align="left"><b>Assigned To</b></TableCell>
              <TableCell align="center"><b>Status</b></TableCell>
              <TableCell align="center"><b>Work Hours</b></TableCell>
              <TableCell align="center"><b>Time Sheet</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedData.map((task, index) => (
              <TableRow key={index}>
                <TableCell align="center">{task.id}</TableCell>
                <TableCell align="left">{task.taskName}</TableCell>
                <TableCell align="left">{`${task.employeeId} - ${task.assignedTo}`}</TableCell>
                <TableCell align="center">
                  <Box sx={getStatusStyle(task.status)}>
                    {task.status}
                  </Box>
                </TableCell>
                <TableCell align="center">{task.workHours}</TableCell>
                <TableCell align="center">
                  <Button variant='outlined' onClick={() => handleTimesheetClick(task.id)}>
                    TimeSheet
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 1, marginBottom: 2 }}>
          <TablePagination
            component="div"
            count={tasks.length}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25]} // Example options
          />
        </Box>
      </Box>
      
      {/* Timesheet Dialog */}
      <TimesheetDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        taskId={selectedTaskId} // Pass the task ID to TimesheetDialog
      />
    </div>
  );
};

export default Tasks;
